import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { getThemeColor } from '@/shared/hooks/useThemeColors'
import { polarToXY } from './svgMath'

interface MasterEyeProps {
  size?: number
  className?: string
  onBlinkComplete?: () => void
}

/**
 * Large animated eye with periodic blinking.
 *
 * Lifecycle:
 *   0.0s  — Eye closed → opens (initial load)
 *   8-12s — Blink: close (200ms) → hold (150ms) → open (300ms)
 *   After reopen → calls onBlinkComplete (triggers map ping)
 *   Repeat every 8-12s (randomized)
 */

const CX = 60
const CY = 60

const particles = [
  { angle: 0, dist: 52, dur: '4.0s', delay: '1.4s' },
  { angle: 60, dist: 48, dur: '3.6s', delay: '1.8s' },
  { angle: 120, dist: 50, dur: '4.3s', delay: '2.2s' },
  { angle: 180, dist: 52, dur: '3.8s', delay: '1.6s' },
  { angle: 240, dist: 48, dur: '4.5s', delay: '2.5s' },
  { angle: 300, dist: 50, dur: '3.4s', delay: '2.0s' },
  { angle: 30, dist: 46, dur: '4.1s', delay: '2.8s' },
  { angle: 210, dist: 47, dur: '3.9s', delay: '3.0s' },
] as const

function particleStart(angle: number, dist: number) {
  return polarToXY(CX, CY, angle, dist)
}

export function MasterEye({ size = 96, className, onBlinkComplete }: MasterEyeProps) {
  const id = useId()
  const [isBlinking, setIsBlinking] = useState(false)
  const callbackRef = useRef(onBlinkComplete)

  useEffect(() => {
    callbackRef.current = onBlinkComplete
  }, [onBlinkComplete])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    let holdTimer: ReturnType<typeof setTimeout>
    let active = true

    function scheduleBlink() {
      if (!active) return
      const delay = 8000 + Math.random() * 4000
      timer = setTimeout(() => {
        if (!active) return
        setIsBlinking(true)
        holdTimer = setTimeout(() => {
          if (!active) return
          setIsBlinking(false)
          callbackRef.current?.()
          scheduleBlink()
        }, 350)
      }, delay)
    }

    // Start after initial open animation
    const startTimer = setTimeout(() => scheduleBlink(), 2500)
    return () => {
      active = false
      clearTimeout(startTimer)
      clearTimeout(timer)
      clearTimeout(holdTimer)
    }
  }, [])

  const strokeColor = getThemeColor('--chart-stroke')
  const pupilFill = getThemeColor('--chart-pupil')
  const glowColor = getThemeColor('--chart-glow-strong')
  const particleColor = getThemeColor('--chart-particle')

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      fill="none"
      className={cn('master-eye', isBlinking && 'master-eye--blinking', className)}
      aria-label="Omni Sight"
    >
      <defs>
        {/* Pupil glow — larger, stronger */}
        <filter id={`${id}-glow`} x="-80%" y="-80%" width="260%" height="260%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="4"
            floodColor={glowColor}
            floodOpacity="1"
          />
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="8"
            floodColor={glowColor}
            floodOpacity="0.4"
          />
        </filter>
        {/* Iris outer glow */}
        <filter id={`${id}-iris-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="3"
            floodColor={glowColor}
            floodOpacity="0.5"
          />
        </filter>
        {/* Particle glow */}
        <filter id={`${id}-pglow`} x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="2"
            floodColor={particleColor}
            floodOpacity="0.8"
          />
        </filter>
      </defs>

      {/* Upper eyelid */}
      <path
        className="master-lid-upper"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />

      {/* Lower eyelid */}
      <path
        className="master-lid-lower"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />

      {/* Iris ring — with glow filter */}
      <circle
        cx={CX}
        cy={CY}
        r="18"
        stroke={strokeColor}
        strokeWidth="0.8"
        fill="none"
        filter={`url(#${id}-iris-glow)`}
        className="master-iris"
      />

      {/* Inner iris detail ring */}
      <circle
        cx={CX}
        cy={CY}
        r="12"
        stroke={strokeColor}
        strokeWidth="0.4"
        strokeOpacity="0.3"
        fill="none"
        className="master-iris"
      />

      {/* Pupil with double glow */}
      <circle
        cx={CX}
        cy={CY}
        r="7"
        fill={pupilFill}
        filter={`url(#${id}-glow)`}
        className="master-pupil"
      />

      {/* Data ingestion particles */}
      {particles.map((p, i) => {
        const start = particleStart(p.angle, p.dist)
        const path = `M ${start.x},${start.y} L ${CX},${CY}`
        return (
          <circle key={i} r="1.5" fill={particleColor} className="master-particle">
            <animateMotion
              path={path}
              dur={p.dur}
              begin={p.delay}
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.4 0 0.2 1"
              keyTimes="0;1"
            />
            <animate
              attributeName="opacity"
              values="0;0.8;0.8;0"
              keyTimes="0;0.12;0.7;1"
              dur={p.dur}
              begin={p.delay}
              repeatCount="indefinite"
            />
            <animate
              attributeName="r"
              values="1.5;1.5;0.5"
              keyTimes="0;0.6;1"
              dur={p.dur}
              begin={p.delay}
              repeatCount="indefinite"
            />
          </circle>
        )
      })}
    </svg>
  )
}
